import { Form, Formik, FormikErrors } from 'formik';

import { Button } from '../../cdl/Button/Button';
import { DrawerContentLayout } from '../../cdl/Drawer/DrawerContentLayout';
import { FormikInput } from '../../cdl/Input/FormikInput';
import { Text } from '../../cdl/Text/Text';
import { FormikDebug } from '../../common/form-elements/formik/FormikDebug';
import { translate } from '../../common/helpers/translate.helper';
import { Characters } from '../../common/helpers/unicode.helper';
import { FormikMoneyInput } from '../../common/MoneyInput/FormikMoneyInput';

import { Waiver } from './WaiverButton';

const NegativeDollar = () => {
    return (
        <span>
            {Characters.DOLLAR}
            {Characters.NO_BREAK_SPACE}
            {Characters.MINUS}
        </span>
    );
};

interface WaiverFormProps {
    onSubmit: (waiver: Waiver) => void;
    data?: Waiver;
}

interface WaiverFormState {
    description: string;
    amount: string;
}

export const WaiverForm = ({ onSubmit, data }: WaiverFormProps) => {
    const initialValues: WaiverFormState = {
        description: data?.description ?? '',
        amount: data?.amount ?? '',
    };

    const validateWaiverForm = (values: WaiverFormState): FormikErrors<WaiverFormState> => {
        const errors: FormikErrors<WaiverFormState> = {};

        if (!values.description) {
            errors.description = translate('waiverForm.errors.descriptionRequired');
        }

        if (!values.amount) {
            errors.amount = translate('waiverForm.errors.amountRequired');
        }

        if (Number(values.amount) === 0) {
            errors.amount = translate('waiverForm.errors.amountZero');
        }

        return errors;
    };

    return (
        <Formik initialValues={initialValues} validate={validateWaiverForm} onSubmit={onSubmit}>
            {({ isSubmitting, dirty }) => (
                <Form style={{ height: '100%', minHeight: 0 }}>
                    <DrawerContentLayout
                        isBusy={isSubmitting}
                        hasUnsavedChanges={dirty}
                        primaryAction={
                            <Button type="submit" emphasis="high" isLoading={isSubmitting}>
                                {translate('waiverForm.button')}
                            </Button>
                        }
                    >
                        <Text variant="body" color="foreground.muted" as="p" style={{ whiteSpace: 'pre-wrap' }}>
                            {translate('waiverForm.description.amount')}
                            <br />
                            {translate('waiverForm.description.total')}
                        </Text>

                        <FormikInput name="description" label={translate('waiverForm.labels.description')} />
                        <FormikMoneyInput
                            name="amount"
                            label={translate('waiverForm.labels.amount')}
                            decimals={2}
                            allowNegativeValue={false}
                            leadingVisual={<NegativeDollar />}
                        />
                        <FormikDebug />
                    </DrawerContentLayout>
                </Form>
            )}
        </Formik>
    );
};
